import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { breakTypeSchema, BreakTypeFormData } from "@/schemas/shiftSchemas";
import { BreakType } from "@/types/shifts";
import { useCreateBreakType, useUpdateBreakType } from "@/hooks/useShifts";

interface BreakTypeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  breakType?: BreakType | null;
}

export function BreakTypeFormDialog({
  open,
  onOpenChange,
  breakType,
}: BreakTypeFormDialogProps) {
  const { toast } = useToast();
  const createMutation = useCreateBreakType();
  const updateMutation = useUpdateBreakType();
  const isLoading = createMutation.isPending || updateMutation.isPending;

  const form = useForm<BreakTypeFormData>({
    resolver: zodResolver(breakTypeSchema),
    defaultValues: {
      name: "",
      code: "",
      default_duration_minutes: 30,
      is_paid: false,
      is_active: true,
    },
  });

  useEffect(() => {
    if (breakType) {
      form.reset({
        name: breakType.name,
        code: breakType.code,
        default_duration_minutes: breakType.default_duration_minutes,
        is_paid: breakType.is_paid,
        is_active: breakType.is_active,
      });
    } else {
      form.reset({
        name: "",
        code: "",
        default_duration_minutes: 30,
        is_paid: false,
        is_active: true,
      });
    }
  }, [breakType, form, open]);

  const onSubmit = async (data: BreakTypeFormData) => {
    try {
      if (breakType) {
        await updateMutation.mutateAsync({ id: breakType.id, ...data });
        toast({
          title: "Tipo de descanso actualizado",
          description: "El tipo de descanso se ha actualizado correctamente",
        });
      } else {
        await createMutation.mutateAsync(data);
        toast({
          title: "Tipo de descanso creado",
          description: "El tipo de descanso se ha creado correctamente",
        });
      }
      onOpenChange(false);
      form.reset();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Error al guardar el tipo de descanso",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {breakType ? "Editar Tipo de Descanso" : "Nuevo Tipo de Descanso"}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre</FormLabel>
                  <FormControl>
                    <Input placeholder="Almuerzo" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Código</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="LUNCH"
                      {...field}
                      onChange={(e) =>
                        field.onChange(e.target.value.toUpperCase())
                      }
                    />
                  </FormControl>
                  <FormDescription>Identificador único (ej: LUNCH, COFFEE)</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="default_duration_minutes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Duración por defecto (minutos)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={1}
                      max={120}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex gap-6">
              <FormField
                control={form.control}
                name="is_paid"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="!mt-0 cursor-pointer">
                      Descanso pagado
                    </FormLabel>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="is_active"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="!mt-0 cursor-pointer">
                      Activo
                    </FormLabel>
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {breakType ? "Actualizar" : "Crear"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
